/**
 * Magnetic Buttons
 *
 * Creates a magnetic hover effect where elements follow the cursor.
 * Based on: https://codepen.io/tdesero/pen/RmoxQg
 *
 * Usage:
 * ------
 * 1. Add the 'magnetic' class to any element you want to be magnetic
 * 2. Optionally add data attributes to control strength:
 *    - data-strength="50"      (overall magnet strength, default: 50)
 *    - data-strength-text="25" (inner text element strength, default: 25)
 * 3. Add a child element with class 'btn-text' for independent text movement
 * 4. Add a child element with class 'btn-fill' for hover fill effect
 *
 * HTML Example:
 * -------------
 * <button class="magnetic" data-strength="50" data-strength-text="25">
 *   <span class="btn-fill"></span>
 *   <span class="btn-text">Click me</span>
 * </button>
 *
 * Initialize:
 * -----------
 * import MagneticButtons from './magnetic-buttons.js';
 *
 * // With defaults
 * const magnetic = new MagneticButtons();
 *
 * // With options
 * const magnetic = new MagneticButtons({
 *   selector: '.magnetic',
 *   strength: 50,
 *   strengthText: 25,
 *   minWidth: 540,
 *   ease: 'elastic.out(1, 0.3)',
 *   duration: 1.5,
 * });
 *
 * // Destroy when needed
 * magnetic.destroy();
 */

import gsap from 'gsap';

class MagneticButtons {
	constructor(options = {}) {
		this.options = {
			selector: '.magnetic',
			strength: 50,
			strengthText: 25,
			minWidth: 540,
			ease: 'elastic.out(1, 0.3)',
			duration: 1.5,
			fillSelector: '.btn-fill',
			textSelector: '.btn-text',
			...options,
		};

		this.magnets = [];
		this.boundHandlers = new Map();

		this.init();
	}

	init() {
		this.magnets = document.querySelectorAll(this.options.selector);

		if (!this.magnets.length) return;

		this.magnets.forEach((magnet) => {
			this.setupMagnet(magnet);
		});
	}

	setupMagnet(magnet) {
		const handlers = {
			mouseMove: (e) => this.onMouseMove(e, magnet),
			mouseLeave: (e) => this.onMouseLeave(e, magnet),
			mouseEnter: (e) => this.onMouseEnter(e, magnet),
		};

		this.boundHandlers.set(magnet, handlers);

		// Only apply magnetic effect on wider screens
		if (window.innerWidth > this.options.minWidth) {
			magnet.addEventListener('mousemove', handlers.mouseMove);
			magnet.addEventListener('mouseleave', handlers.mouseLeave);
		}

		// Fill and text animations work on all screen sizes
		magnet.addEventListener('mouseenter', handlers.mouseEnter);
		magnet.addEventListener('mouseleave', handlers.mouseLeave);
	}

	onMouseMove(e, magnet) {
		const bounding = magnet.getBoundingClientRect();
		const strength = magnet.dataset.strength || this.options.strength;
		const strengthText = magnet.dataset.strengthText || this.options.strengthText;

		const relX = ( e.clientX - bounding.left ) / magnet.offsetWidth - 0.5;
		const relY = ( e.clientY - bounding.top ) / magnet.offsetHeight - 0.5;

		gsap.to(magnet, {
			x: relX * strength,
			y: relY * strength,
			rotate: '0.001deg',
			duration: this.options.duration,
			ease: 'power4.out',
		});

		const textEl = magnet.querySelector(this.options.textSelector);
		if (textEl) {
			gsap.to(textEl, {
				x: relX * strengthText,
				y: relY * strengthText,
				rotate: '0.001deg',
				duration: this.options.duration,
				ease: 'power4.out',
			});
		}
	}

	onMouseLeave(e, magnet) {
		// Reset magnetic position
		if (window.innerWidth > this.options.minWidth) {
			gsap.to(magnet, {
				x: 0,
				y: 0,
				duration: this.options.duration,
				ease: this.options.ease,
			});

			const textEl = magnet.querySelector(this.options.textSelector);
			if (textEl) {
				gsap.to(textEl, {
					x: 0,
					y: 0,
					duration: this.options.duration,
					ease: this.options.ease,
				});
			}
		}

		// Animate fill out
		const fillEl = magnet.querySelector(this.options.fillSelector);
		if (fillEl) {
			gsap.to(fillEl, {
				y: '-76%',
				duration: 0.6,
				ease: 'power2.inOut',
			});
		}
	}

	onMouseEnter(e, magnet) {
		// Animate fill in
		const fillEl = magnet.querySelector(this.options.fillSelector);
		if (fillEl) {
			gsap.fromTo(
					fillEl,
					{ y: '76%' },
					{
						y: '0%',
						duration: 0.6,
						ease: 'power2.inOut',
					},
			);
		}
	}

	/**
	 * Refresh the magnetic buttons (e.g., after DOM changes)
	 */
	refresh() {
		this.destroy();
		this.init();
	}

	/**
	 * Clean up all event listeners
	 */
	destroy() {
		this.magnets.forEach((magnet) => {
			const handlers = this.boundHandlers.get(magnet);
			if (handlers) {
				magnet.removeEventListener('mousemove', handlers.mouseMove);
				magnet.removeEventListener('mouseleave', handlers.mouseLeave);
				magnet.removeEventListener('mouseenter', handlers.mouseEnter);
			}

			// Reset any transforms
			gsap.set(magnet, { clearProps: 'x,y,rotate' });

			const textEl = magnet.querySelector(this.options.textSelector);
			if (textEl) {
				gsap.set(textEl, { clearProps: 'x,y,rotate' });
			}
		});

		this.boundHandlers.clear();
		this.magnets = [];
	}
}

export default MagneticButtons;
